import React, { useState, useEffect } from 'react';
import { 
  collection, 
  query, 
  orderBy, 
  getDocs, 
  doc, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  serverTimestamp,
  where
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { FIREBASE_DB, FIREBASE_STORAGE } from '../firebase.config';
import { Edit, Trash, PlusCircle, Image, EyeOff, Eye, MoveUp, MoveDown } from 'lucide-react';

const BannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [currentBanner, setCurrentBanner] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Initial form state
  const initialBannerState = {
    title: '',
    subtitle: '',
    buttonText: '',
    buttonLink: '',
    height: '200px',
    backgroundColor: '#f0f0f0',
    isActive: true,
    order: 0,
    isTextOnly: false,
    scrollText: false
  };

  // Fetch banners from Firestore
  const fetchBanners = async () => {
    try {
      setLoading(true);
      
      // For admin users, we want to get all banners, both active and inactive
      // According to the security rules, the admin needs to be authenticated and email verified
      const bannersQuery = query(
        collection(FIREBASE_DB, 'banners'),
        orderBy('order', 'asc')
      );
      
      const querySnapshot = await getDocs(bannersQuery);
      const bannersList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      setBanners(bannersList);
    } catch (error) {
      console.error('Error fetching banners:', error);
      
      // If there's a permission error, try fetching only active banners as a fallback
      try {
        const activeOnlyQuery = query(
          collection(FIREBASE_DB, 'banners'),
          where('isActive', '==', true),
          orderBy('order', 'asc')
        );
        
        const activeSnapshot = await getDocs(activeOnlyQuery);
        const activeBanners = activeSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        setBanners(activeBanners);
        alert('Note: You only have access to view active banners. Some admin features may be limited.');
      } catch (fallbackError) {
        console.error('Fallback fetch also failed:', fallbackError);
        alert('Error fetching banners. Please ensure you have proper permissions.');
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  // Handle image file selection
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setBannerFile(file);
      
      // Create a preview URL
      const fileReader = new FileReader();
      fileReader.onload = () => {
        setPreviewUrl(fileReader.result);
      };
      fileReader.readAsDataURL(file);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentBanner(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Start editing a banner
  const handleEdit = (banner) => {
    setCurrentBanner(banner);
    setPreviewUrl(banner.imageUrl || '');
    setEditMode(true);
  };

  // Start creating a new banner
  const handleCreateNew = () => {
    setCurrentBanner({
      ...initialBannerState,
      order: banners.length // Set order to the end of the list
    });
    setPreviewUrl('');
    setBannerFile(null);
    setEditMode(true);
  };

  // Cancel editing/creating
  const handleCancel = () => {
    setEditMode(false);
    setCurrentBanner(null);
    setPreviewUrl('');
    setBannerFile(null);
  };

  // Handle form submission (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!currentBanner) return;
    
    try {
      setIsSubmitting(true);
      
      let imageUrl = currentBanner.imageUrl || '';
      let imagePath = currentBanner.imagePath || '';
      
      // If it's a text-only banner, we don't need an image
      if (currentBanner.isTextOnly) {
        imageUrl = '';
        imagePath = '';
      } 
      // If there's a new file, upload it
      else if (bannerFile) {
        try {
          // Upload to banners directory
          const storageRef = ref(FIREBASE_STORAGE, `banners/${Date.now()}-${bannerFile.name}`);
          await uploadBytes(storageRef, bannerFile);
          imageUrl = await getDownloadURL(storageRef);
          imagePath = `banners/${Date.now()}-${bannerFile.name}`;
          console.log('Uploaded to banners directory successfully');
        } catch (uploadError) {
          console.warn('Firebase Storage direct upload failed:', uploadError);
          
          // Try uploading to test directory as fallback
          try {
            const testStorageRef = ref(FIREBASE_STORAGE, `test/${Date.now()}-${bannerFile.name}`);
            await uploadBytes(testStorageRef, bannerFile);
            imageUrl = await getDownloadURL(testStorageRef);
            imagePath = `test/${Date.now()}-${bannerFile.name}`;
            console.log('Uploaded to test directory as fallback');
          } catch (testUploadError) {
            console.error('All upload attempts failed:', testUploadError);
            
            // For now, we'll use a placeholder URL or existing image
            imageUrl = currentBanner.imageUrl || 'https://via.placeholder.com/800x400?text=Banner+Image';
            alert('Image upload failed. You may not have sufficient permissions. Using placeholder or existing image instead.');
          }
        }
        
        // If updating and there was a previous image, try to delete it
        if (currentBanner.id && currentBanner.imagePath) {
          try {
            const oldImageRef = ref(FIREBASE_STORAGE, currentBanner.imagePath);
            await deleteObject(oldImageRef);
          } catch (error) {
            console.error('Error deleting old image:', error);
          }
        }
      } else if (!imageUrl && !currentBanner.isTextOnly) {
        // If no file is uploaded, no existing image, and not text-only, use a placeholder
        imageUrl = 'https://via.placeholder.com/800x400?text=Banner+Image';
        imagePath = '';
      }
      
      const bannerData = {
        ...currentBanner,
        imageUrl,
        imagePath,
        updatedAt: serverTimestamp()
      };
      
      delete bannerData.id; // Remove id before saving to Firestore
      
      if (currentBanner.id) {
        // Update existing banner
        await updateDoc(doc(FIREBASE_DB, 'banners', currentBanner.id), bannerData);
      } else {
        // Create new banner
        bannerData.createdAt = serverTimestamp();
        const docRef = await addDoc(collection(FIREBASE_DB, 'banners'), bannerData);
      }
      
      // Reset form and refresh list
      setEditMode(false);
      setCurrentBanner(null);
      setPreviewUrl('');
      setBannerFile(null);
      await fetchBanners();
      
    } catch (error) {
      console.error('Error saving banner:', error);
      alert('Failed to save banner. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Delete a banner
  const handleDelete = async (banner) => {
    if (!window.confirm(`Are you sure you want to delete the banner "${banner.title || 'Untitled'}"?`)) {
      return;
    }
    
    try {
      setLoading(true);
      
      // Delete the image from storage if it exists
      if (banner.imagePath) {
        try {
          const imageRef = ref(FIREBASE_STORAGE, banner.imagePath);
          await deleteObject(imageRef);
        } catch (error) {
          console.error('Error deleting image:', error);
        }
      }
      
      // Delete the banner document
      await deleteDoc(doc(FIREBASE_DB, 'banners', banner.id));
      
      // Refresh the list
      await fetchBanners();
      
    } catch (error) {
      console.error('Error deleting banner:', error);
      alert('Failed to delete banner. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Toggle banner active status
  const toggleActive = async (banner) => {
    try {
      await updateDoc(doc(FIREBASE_DB, 'banners', banner.id), {
        isActive: !banner.isActive,
        updatedAt: serverTimestamp()
      });
      await fetchBanners();
    } catch (error) {
      console.error('Error toggling banner status:', error);
      alert('Failed to update banner status. Please try again.');
    }
  };

  // Move banner up in order
  const moveBannerUp = async (banner, index) => {
    if (index === 0) return; // Already at the top
    
    try {
      setLoading(true);
      
      // Get the banner above this one
      const aboveBanner = banners[index - 1];
      
      // Swap orders
      await updateDoc(doc(FIREBASE_DB, 'banners', banner.id), {
        order: aboveBanner.order,
        updatedAt: serverTimestamp()
      });
      
      await updateDoc(doc(FIREBASE_DB, 'banners', aboveBanner.id), {
        order: banner.order,
        updatedAt: serverTimestamp()
      });
      
      await fetchBanners();
    } catch (error) {
      console.error('Error moving banner:', error);
      alert('Failed to reorder banner. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Move banner down in order
  const moveBannerDown = async (banner, index) => {
    if (index === banners.length - 1) return; // Already at the bottom
    
    try {
      setLoading(true);
      
      // Get the banner below this one
      const belowBanner = banners[index + 1];
      
      // Swap orders
      await updateDoc(doc(FIREBASE_DB, 'banners', banner.id), {
        order: belowBanner.order,
        updatedAt: serverTimestamp()
      });
      
      await updateDoc(doc(FIREBASE_DB, 'banners', belowBanner.id), {
        order: banner.order,
        updatedAt: serverTimestamp()
      });
      
      await fetchBanners();
    } catch (error) {
      console.error('Error moving banner:', error);
      alert('Failed to reorder banner. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-gray-800">Banner Management</h2>
        {!editMode && (
          <button
            onClick={handleCreateNew}
            className="flex items-center bg-green-500 text-white px-4 py-2 rounded-md hover:bg-green-600 transition-colors"
          >
            <PlusCircle size={18} className="mr-2" />
            Add New Banner
          </button>
        )}
      </div>

      {editMode ? (
        // Banner Edit Form
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Banner Image</label>
                <div className="flex items-center space-x-4">
                  <label className="cursor-pointer bg-blue-100 text-blue-600 px-4 py-2 rounded-md hover:bg-blue-200 transition-colors flex items-center">
                    <Image size={18} className="mr-2" />
                    Select Image
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </label>
                  {previewUrl && (
                    <span className="text-sm text-green-600">Image selected</span>
                  )}
                </div>
                {previewUrl && (
                  <div className="mt-4 relative">
                    <img
                      src={previewUrl}
                      alt="Banner preview"
                      className="w-full h-40 object-cover rounded-md"
                    />
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Background Color</label>
                <input
                  type="color"
                  name="backgroundColor"
                  value={currentBanner?.backgroundColor || '#f0f0f0'}
                  onChange={handleInputChange}
                  className="h-10 w-full rounded-md border-gray-300 shadow-sm"
                />
                <p className="text-xs text-gray-500 mt-1">Used if image is not available or during loading</p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Banner Height</label>
                <select
                  name="height"
                  value={currentBanner?.height || '200px'}
                  onChange={handleInputChange}
                  className="w-full rounded-md border-gray-300 shadow-sm"
                >
                  <option value="150px">Small (150px)</option>
                  <option value="200px">Medium (200px)</option>
                  <option value="300px">Large (300px)</option>
                  <option value="400px">Extra Large (400px)</option>
                </select>
              </div>

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  name="isActive"
                  checked={currentBanner?.isActive || false}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-accent focus:ring-accent border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                  Active (Display on Website)
                </label>
              </div>

              <div className="flex items-center mt-2">
                <input
                  type="checkbox"
                  id="isTextOnly"
                  name="isTextOnly"
                  checked={currentBanner?.isTextOnly || false}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-accent focus:ring-accent border-gray-300 rounded"
                />
                <label htmlFor="isTextOnly" className="ml-2 block text-sm text-gray-700">
                  Text-only Banner (No Image Required)
                </label>
              </div>
              
              {currentBanner?.isTextOnly && (
                <div className="flex items-center mt-2">
                  <input
                    type="checkbox"
                    id="scrollText"
                    name="scrollText"
                    checked={currentBanner?.scrollText || false}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-accent focus:ring-accent border-gray-300 rounded"
                  />
                  <label htmlFor="scrollText" className="ml-2 block text-sm text-gray-700">
                    Enable Scrolling Text
                  </label>
                </div>
              )}
            </div>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  type="text"
                  name="title"
                  value={currentBanner?.title || ''}
                  onChange={handleInputChange}
                  placeholder="Enter banner title"
                  className="w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subtitle</label>
                <textarea
                  name="subtitle"
                  value={currentBanner?.subtitle || ''}
                  onChange={handleInputChange}
                  placeholder="Enter banner subtitle (optional)"
                  rows={3}
                  className="w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Button Text</label>
                <input
                  type="text"
                  name="buttonText"
                  value={currentBanner?.buttonText || ''}
                  onChange={handleInputChange}
                  placeholder="Enter button text (optional)"
                  className="w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Button Link</label>
                <input
                  type="text"
                  name="buttonLink"
                  value={currentBanner?.buttonLink || ''}
                  onChange={handleInputChange}
                  placeholder="Enter button link URL (optional)"
                  className="w-full rounded-md border-gray-300 shadow-sm"
                />
              </div>
            </div>
          </div>

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={handleCancel}
              className="bg-gray-200 text-gray-800 px-6 py-2 rounded-md hover:bg-gray-300 transition-colors"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="bg-primary text-white px-6 py-2 rounded-md hover:bg-primary-dark transition-colors"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : currentBanner?.id ? 'Update Banner' : 'Create Banner'}
            </button>
          </div>
        </form>
      ) : (
        // Banner list
        <div>
          {loading ? (
            <p className="text-center py-8 text-gray-500">Loading banners...</p>
          ) : banners.length === 0 ? (
            <div className="text-center py-12 border border-dashed border-gray-300 rounded-lg">
              <Image size={48} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 mb-4">No banners found</p>
              <button
                onClick={handleCreateNew}
                className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-dark transition-colors"
              >
                Create Your First Banner
              </button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Banner
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Title
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Order
                    </th>
                    <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {banners.map((banner, index) => (
                    <tr key={banner.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        {banner.isTextOnly ? (
                          <div 
                            className="h-12 w-24 rounded flex items-center justify-center text-xs text-gray-500 bg-blue-50 border border-blue-100"
                            style={{backgroundColor: banner.backgroundColor}}
                          >
                            Text Only {banner.scrollText && '(Scrolling)'}
                          </div>
                        ) : banner.imageUrl ? (
                          <img 
                            src={banner.imageUrl} 
                            alt={banner.title || 'Banner'} 
                            className="h-12 w-24 object-cover rounded"
                          />
                        ) : (
                          <div 
                            className="h-12 w-24 rounded flex items-center justify-center text-xs text-gray-500 bg-gray-100"
                          >
                            No image
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-gray-900">{banner.title || 'Untitled Banner'}</div>
                        {banner.subtitle && (
                          <div className="text-xs text-gray-500 truncate max-w-xs">{banner.subtitle}</div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                          banner.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {banner.isActive ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => moveBannerUp(banner, index)}
                            disabled={index === 0}
                            className={`p-1 rounded ${
                              index === 0 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-200'
                            }`}
                            title="Move up"
                          >
                            <MoveUp size={16} />
                          </button>
                          <button
                            onClick={() => moveBannerDown(banner, index)}
                            disabled={index === banners.length - 1}
                            className={`p-1 rounded ${
                              index === banners.length - 1 ? 'text-gray-300 cursor-not-allowed' : 'text-gray-500 hover:bg-gray-200'
                            }`}
                            title="Move down"
                          >
                            <MoveDown size={16} />
                          </button>
                          <span>{banner.order}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={() => toggleActive(banner)}
                            className={`p-1.5 rounded ${
                              banner.isActive 
                                ? 'bg-yellow-100 text-yellow-700 hover:bg-yellow-200' 
                                : 'bg-green-100 text-green-700 hover:bg-green-200'
                            }`}
                            title={banner.isActive ? 'Deactivate' : 'Activate'}
                          >
                            {banner.isActive ? <EyeOff size={16} /> : <Eye size={16} />}
                          </button>
                          <button
                            onClick={() => handleEdit(banner)}
                            className="p-1.5 bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                            title="Edit"
                          >
                            <Edit size={16} />
                          </button>
                          <button
                            onClick={() => handleDelete(banner)}
                            className="p-1.5 bg-red-100 text-red-700 rounded hover:bg-red-200"
                            title="Delete"
                          >
                            <Trash size={16} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BannerManagement; 